/**
 * Dragging a lav or the aggregat in the 3D scene.
 *
 * This controller contributes ONE thing the plan view did not need: turning a
 * raycast hit into a model-frame millimetre point. Everything after that is
 * `resolvePlanDrag` — the same pure pipeline the plan view runs —
 *
 *     pointer -> pure transform -> reconcileInteriorLayout -> validateInteriorLayout
 *
 * so there is no clearance number, no bounds check and no snap step in this file.
 * That is deliberate and it is the whole safety argument for a second editing
 * surface: if the 3D drag decided any of those for itself, the two surfaces would
 * eventually disagree about the same design and the BOM would depend on which one
 * you used. See `planDragModel.ts` and the 2026-07-30 note.
 *
 * The other inherited rule is that the store is never touched during a gesture.
 * `useBuilderStore`'s `temporal()` has no `handleSet` and no `equality`, so zundo
 * records EVERY set: "one drag = one undo step" holds only because the preview is
 * transient THREE geometry and `onCommit` fires exactly once, on release. A
 * release on an invalid position commits nothing — which IS the snap-back, since
 * the store never moved — and a release that changed nothing is skipped too, or a
 * nudge that lands where it started would burn a step that restores nothing.
 *
 * Frame of reference: the interior domain's model frame IS the engines' world
 * frame (X across, Z along, origin at the building centre) scaled mm -> m, so the
 * conversion is a division. The pick plane and the ghost are parented to the
 * ROTATED sauna rig and `hit.point` is converted back through the same node —
 * the same correction `OpeningDragController` needed once the building could turn.
 */
import * as THREE from "three";
import {
	type HeaterSizeMm,
	type InteriorLayout,
	type InteriorLayoutIssue,
	interiorLayoutsEqual,
	benchSeatTopMm,
	setBenchGroupSeatTopMm,
	snapMm,
} from "../../domain/interior";
import { UNIT_BOX } from "../../engines/geometry";
import type { InteriorDragTarget } from "./interiorDragTarget";
import { type InteriorEditSetup, interiorElementBoxM } from "./interiorHandles";
import {
	type PlanDragOrigin,
	planDragOriginValueMm,
	pointAlongTargetMm,
	resolvePlanDrag,
	resolveInteriorEdit,
	wallHintForTarget,
} from "./PlanView/planDragModel";

export type { InteriorDragTarget } from "./interiorDragTarget";

/** Semantic overlay colours — mirrors `HeaterClearanceOverlay` (tokens.css). */
const COLOR_OK = 0x5a6b50;
const COLOR_VIOLATION = 0xd84848;

/**
 * What the viewer shows while a gesture is live.
 *
 * The layout is the CANDIDATE, not the stored design: the heater-clearance
 * envelope is re-resolved against it so the safety zone follows the ghost instead
 * of staying around the committed position (the failure issue 131 documents).
 */
export interface InteriorDragPreview {
	target: InteriorDragTarget;
	valid: boolean;
	layout: InteriorLayout;
	/** The issue this gesture introduced, for the live explanation. */
	issue: InteriorLayoutIssue | null;
	/**
	 * The aggregat's footprint, so a consumer can rebuild the candidate's own
	 * measurement target without being handed the whole edit setup.
	 */
	heaterSizeMm: HeaterSizeMm;
}

export interface InteriorDragConfig {
	/**
	 * The rotated sauna rig. The pick plane and the ghost are built in MODEL
	 * space and inherit the rotation from it; falls back to the scene root.
	 */
	parent: THREE.Object3D;
	camera: THREE.PerspectiveCamera;
	renderer: THREE.WebGLRenderer;
	target: InteriorDragTarget;
	/**
	 * The layout, the rooms, the validation context and the engine-owned heights,
	 * as ONE object.
	 *
	 * Passed whole rather than spread into six fields because they are not
	 * independent: `dimensions` IS `model.dragContext.dimensions` and `rooms` IS
	 * `interiorRooms(model.context)`, so spreading them invited a caller to supply a
	 * combination that cannot occur. It is also the identical object
	 * `InteriorEditHandles` draws from, which is what makes the ghost and the handle
	 * agree by construction.
	 */
	setup: InteriorEditSetup;
	/**
	 * Client position of the POINTERDOWN that opened the gesture — not of the
	 * move that crossed the drag threshold.
	 *
	 * This is what anchors the drag. `resolvePlanDrag` applies a snapped DELTA to
	 * the element's stored value, so anchoring on the wrong pixel would shift the
	 * element by the threshold distance on every grab, and anchoring on nothing at
	 * all would teleport its centre to the finger.
	 */
	anchorClient: { x: number; y: number };
	onCommit: (layout: InteriorLayout) => void;
	onCancel: () => void;
	onPreviewChange?: (preview: InteriorDragPreview | null) => void;
	/** Ask the demand-mode frameloop for a frame. */
	onDragUpdate?: () => void;
}

export class InteriorDragController {
	private parent: THREE.Object3D;
	private camera: THREE.PerspectiveCamera;
	private domElement: HTMLElement;
	private config: InteriorDragConfig;

	private raycaster = new THREE.Raycaster();
	private pointer = new THREE.Vector2();
	private heightOrigin: { pointerMm: number; valueMm: number } | null = null;
	private pickPlane: THREE.Mesh | null = null;
	/** Single-element raycast target list, built once rather than per event. */
	private pickTargets: THREE.Object3D[] = [];
	/** Scratch for the world→model conversion; `worldToLocal` mutates in place. */
	private hitPoint = new THREE.Vector3();
	private ghost: THREE.Mesh | null = null;
	private groupGhosts: { runId: string; mesh: THREE.Mesh }[] = [];
	private isDragging = false;
	private activePointerId: number | null = null;
	private origin: PlanDragOrigin | null = null;
	/**
	 * The last PUBLISHED preview — the one the viewer is currently showing.
	 *
	 * One field, not two. A separate "latest" was redundant: `publish` only keeps
	 * this value when the incoming preview is equivalent to it, so the two were
	 * always content-equal.
	 */
	private published: InteriorDragPreview | null = null;

	private boundPointerMove: (event: PointerEvent) => void;
	private boundPointerUp: (event: PointerEvent) => void;
	private boundPointerCancel: (event: PointerEvent) => void;
	private boundKeyDown: (event: KeyboardEvent) => void;

	constructor(config: InteriorDragConfig) {
		this.config = config;
		this.parent = config.parent;
		this.camera = config.camera;
		this.domElement = config.renderer.domElement;

		this.boundPointerMove = this.handlePointerMove.bind(this);
		this.boundPointerUp = this.handlePointerUp.bind(this);
		this.boundPointerCancel = this.handlePointerCancel.bind(this);
		this.boundKeyDown = this.handleKeyDown.bind(this);
	}

	startDrag(pointerId?: number): void {
		if (this.isDragging) return;
		this.isDragging = true;
		this.activePointerId = pointerId ?? null;

		this.buildPickPlane();
		this.createGhost();

		// The anchor is resolved through the SAME plane the gesture will be
		// resolved against, so a grab offset falls out of the arithmetic instead of
		// being a special case per target kind.
		const anchorPoint = this.pointAt(
			this.config.anchorClient.x,
			this.config.anchorClient.y,
		);
		this.anchorAt(anchorPoint);

		this.domElement.addEventListener("pointermove", this.boundPointerMove);
		this.domElement.addEventListener("pointerup", this.boundPointerUp);
		this.domElement.addEventListener("pointercancel", this.boundPointerCancel);
		this.domElement.addEventListener(
			"lostpointercapture",
			this.boundPointerCancel,
		);
		window.addEventListener("keydown", this.boundKeyDown);
		this.domElement.style.cursor = "grabbing";

		// Touch needs the capture: the element keeps receiving events after the
		// finger leaves it, so a drag off the edge of the canvas ends in a release
		// we can see rather than a stuck gesture with orbit still disabled.
		if (pointerId !== undefined) {
			try {
				this.domElement.setPointerCapture(pointerId);
			} catch {
				// jsdom and some touch stacks refuse capture; the canvas' own
				// move/up handlers still carry the gesture.
			}
		}

		// Show the resting state immediately, so a grab reads as "held" before the
		// first move rather than only once something has changed.
		this.publish(this.resolveAt(anchorPoint));
	}

	cancelDrag(): void {
		this.cleanup();
		this.config.onPreviewChange?.(null);
		this.config.onCancel();
	}

	/**
	 * Teardown. Strictly MORE than `cleanup()`: it also removes the invisible pick
	 * plane, which `cleanup()` deliberately leaves alone so a cancel/commit path
	 * that reuses the controller does not have to rebuild it. Every owner must call
	 * this rather than relying on the internal cleanup — the asymmetry is exactly
	 * what leaked scene objects out of the opening pill (2026-07-16 note).
	 */
	dispose(): void {
		this.cleanup();
		if (this.pickPlane) {
			this.pickPlane.removeFromParent();
			this.pickPlane.geometry.dispose();
			(this.pickPlane.material as THREE.Material).dispose();
			this.pickPlane = null;
			this.pickTargets = [];
		}
	}

	/**
	 * A horizontal plane at the grabbed element's own height.
	 *
	 * FINITE on purpose. A camera close to the plane's height sends nearly parallel
	 * rays, and an infinite plane would then return hit points hundreds of metres
	 * away — the element would slam into a corner on every twitch. A bounded plane
	 * simply misses, so the ghost holds still instead.
	 */
	private buildPickPlane(): void {
		const { frame, model } = this.config.setup;
		const { dimensions } = model.dragContext;
		const box = this.boxFor(model.layout);
		const planeY = box ? box.centerM[1] : frame.wallBaseY;
		const span = Math.max(dimensions.width, dimensions.length) * 1.5 + 1;

		const plane = new THREE.Mesh(
			new THREE.PlaneGeometry(span, span),
			new THREE.MeshBasicMaterial({
				visible: false,
				side: THREE.DoubleSide,
			}),
		);
		if (this.config.target.kind === "bench-height" && box) {
			// Camera-facing vertical plane in the MODEL frame; building rotation
			// is handled by the same parent transform used for the hit points.
			const localCamera = this.parent.worldToLocal(
				this.camera.getWorldPosition(new THREE.Vector3()),
			);
			const normal = localCamera.sub(new THREE.Vector3(...box.centerM));
			normal.y = 0;
			if (normal.lengthSq() < 1e-8) normal.set(0, 0, 1);
			plane.quaternion.setFromUnitVectors(
				new THREE.Vector3(0, 0, 1),
				normal.normalize(),
			);
			plane.position.set(...box.centerM);
		} else {
			plane.rotation.x = -Math.PI / 2;
			plane.position.set(0, planeY, 0);
		}
		plane.visible = false;
		this.parent.add(plane);
		// `Raycaster` does NOT refresh world matrices — the renderer normally does it
		// once per frame. The ANCHOR is raycast here in `startDrag`, before R3F has
		// rendered anything, so without this the plane is still at its identity
		// transform: an UNROTATED XY plane through the origin instead of a horizontal
		// one under a possibly-turned rig. Measured: the same ray hits (0.30, 0.77, 0)
		// stale versus (0.46, 0.90, 0.18) correct — so the gesture anchored on a point
		// that is not on the plane it will be resolved against, and the element jumped
		// on the first move. `true` for ancestors: the rig's own matrix matters too.
		plane.updateWorldMatrix(true, false);
		this.pickPlane = plane;
		this.pickTargets = [plane];
	}

	/** The box the target occupies in a candidate layout — handle box and ghost. */
	private boxFor(layout: InteriorLayout) {
		const { frame, model } = this.config.setup;
		return interiorElementBoxM(
			this.config.target,
			layout,
			model.rooms,
			model.context.heaterSize,
			frame,
		);
	}

	private createGhost(): void {
		const ghost = new THREE.Mesh(
			UNIT_BOX,
			new THREE.MeshBasicMaterial({
				color: COLOR_OK,
				transparent: true,
				opacity: 0.42,
				depthTest: false,
				depthWrite: false,
				toneMapped: false,
				side: THREE.DoubleSide,
			}),
		);
		ghost.name = "detail/interaction/interior-ghost";
		ghost.renderOrder = 999;
		ghost.raycast = () => {};
		ghost.visible = false;
		this.parent.add(ghost);
		this.ghost = ghost;
		const target = this.config.target;
		if (target.kind === "bench-height") {
			const runs = this.config.setup.model.layout.benchRuns;
			const selected = runs.find(r => r.id === target.runId);
			if (selected?.heightGroupId) {
				for (const run of runs.filter(r => r.id !== selected.id && r.room === selected.room && r.heightGroupId === selected.heightGroupId)) {
					const mesh = ghost.clone();
					mesh.material = ghost.material.clone();
					mesh.name = `detail/interaction/interior-group-ghost/${run.id}`;
					mesh.raycast = () => {};
					this.parent.add(mesh);
					this.groupGhosts.push({ runId: run.id, mesh });
				}
			}
		}
	}

	/**
	 * Anchor the gesture on a model-frame point, if there is one.
	 *
	 * Returns false when the ray missed and the gesture is still unanchored. A
	 * missed anchor must NOT fall back to `alongMm: 0`: the delta model would then
	 * read the element's current wall coordinate as the movement and reconciliation
	 * would slam it to the far end of the wall on the first frame that did hit. So
	 * the gesture stays unanchored and anchors itself on the first hit instead —
	 * which is also the friendlier behaviour, since a grab from a near-parallel
	 * camera angle then simply starts working once the pointer reaches the plane.
	 */
	private anchorAt(
		point: { xMm: number; zMm: number; yMm: number } | null,
	): boolean {
		if (!point) return false;
		const { target } = this.config;
		const { layout, openings, dragContext: context } = this.config.setup.model;
		if (target.kind === "bench-height") {
			const run = layout.benchRuns.find((run) => run.id === target.runId);
			if (!run) return false;
			const room = this.config.setup.model.rooms.find(
				(room) => room.id === run.room,
			);
			this.heightOrigin = {
				pointerMm: point.yMm,
				valueMm: benchSeatTopMm(run, room?.wallHeightMm),
			};
			return true;
		}
		this.origin = {
			alongMm: pointAlongTargetMm(target, point, layout, openings, context),
			valueMm: planDragOriginValueMm(target, layout, openings),
		};
		return true;
	}

	/** Client pixel -> model-frame millimetres, or null when the ray misses. */
	private pointAt(
		clientX: number,
		clientY: number,
	): { xMm: number; zMm: number; yMm: number } | null {
		if (!this.pickPlane) return null;
		const rect = this.domElement.getBoundingClientRect();
		if (!rect.width || !rect.height) return null;

		this.pointer.x = ((clientX - rect.left) / rect.width) * 2 - 1;
		this.pointer.y = -((clientY - rect.top) / rect.height) * 2 + 1;
		this.raycaster.setFromCamera(this.pointer, this.camera);
		const hits = this.raycaster.intersectObjects(this.pickTargets, false);
		if (hits.length === 0) return null;

		// `intersectObjects` reports WORLD space. The plane was built in model
		// space under a rig that may be turned, so the hit has to come back through
		// the same node or every millimetre is off by the rotation.
		const local = this.parent.worldToLocal(this.hitPoint.copy(hits[0].point));
		return { xMm: local.x * 1000, zMm: local.z * 1000, yMm: local.y * 1000 };
	}

	/** Resolve a pointer position into a candidate, or null when the ray missed. */
	private resolveAt(
		point: { xMm: number; zMm: number; yMm: number } | null,
	): InteriorDragPreview | null {
		if (!point) return null;
		// Still unanchored because the pointerdown ray missed the plane — take this
		// hit as the anchor. The gesture then resolves to a zero delta this frame,
		// i.e. it holds still, which is exactly the intended degradation.
		if (!this.origin && !this.heightOrigin && !this.anchorAt(point))
			return null;
		if (!this.origin && !this.heightOrigin) return null;
		const { target } = this.config;
		const { layout, openings, dragContext: context } = this.config.setup.model;

		if (target.kind === "bench-height") {
			if (!this.heightOrigin) return null;
			const deltaMm = snapMm(point.yMm - this.heightOrigin.pointerMm);
			const candidate =
				setBenchGroupSeatTopMm(layout, target.runId,
					this.heightOrigin.valueMm + deltaMm, this.config.setup.model.rooms);
			const resolution = resolveInteriorEdit({
				layout,
				candidate,
				openings,
				context,
				focusRunId: target.runId,
			});
			return {
				target,
				valid: resolution.valid,
				layout: resolution.layout,
				heaterSizeMm: context.heaterSize,
				issue:
					resolution.issues.find((issue) => issue.severity === "error") ??
					resolution.issues[0] ??
					null,
			};
		}
		if (!this.origin) return null;
		const resolution = resolvePlanDrag({
			target,
			layout,
			openings,
			pointMm: point,
			origin: this.origin,
			context,
			// The "only the aggregat may change wall" rule lives in `planDragModel`
			// beside `resolvePlanDrag`, shared with the plan view — a second copy here
			// would keep the old answer if benches ever gained wall-switching.
			wallHint: wallHintForTarget(target, point, layout, context.room),
		});

		return {
			target,
			valid: resolution.valid,
			layout: resolution.layout,
			heaterSizeMm: this.config.setup.model.context.heaterSize,
			issue:
				resolution.issues.find((entry) => entry.severity === "error") ??
				resolution.issues[0] ??
				null,
		};
	}

	/**
	 * Move the ghost and tell the viewer — but only when something actually
	 * changed.
	 *
	 * The candidate is quantised by the domain's 10 mm snap, so most events of a
	 * slow drag resolve to the layout the previous one already published. The
	 * equality guard has to come FIRST: it used to sit below `syncGhost`, which ends
	 * in `onDragUpdate` → `invalidate()`, so every pointermove woke the demand-mode
	 * frameloop for a full scene render — post chain included — that painted
	 * identical pixels. The guard is the debounce only if nothing above it requests a
	 * frame.
	 */
	private publish(preview: InteriorDragPreview | null): void {
		const previous = this.published;
		const unchanged =
			previous !== null &&
			preview !== null &&
			previous.valid === preview.valid &&
			previous.issue?.code === preview.issue?.code &&
			interiorLayoutsEqual(previous.layout, preview.layout);
		if (unchanged) return;

		this.published = preview;
		this.syncGhost(preview);
		this.config.onPreviewChange?.(preview);
		this.config.onDragUpdate?.();
	}

	private syncGhost(preview: InteriorDragPreview | null): void {
		for (const { runId, mesh } of this.groupGhosts) {
			const { model, frame } = this.config.setup;
			const box = preview ? interiorElementBoxM({ kind: "bench-height", runId }, preview.layout, model.rooms, model.context.heaterSize, frame) : null;
			mesh.visible = Boolean(box);
			if (box) {
				mesh.position.set(...box.centerM);
				mesh.scale.set(...box.sizeM);
				(mesh.material as THREE.MeshBasicMaterial).color.setHex(preview!.valid ? COLOR_OK : COLOR_VIOLATION);
			}
		}
		const ghost = this.ghost;
		if (!ghost) return;
		if (!preview) {
			ghost.visible = false;
			return;
		}

		const box = this.boxFor(preview.layout);
		if (!box || box.sizeM.some((value) => value <= 0)) {
			ghost.visible = false;
			return;
		}

		ghost.position.set(...box.centerM);
		ghost.scale.set(...box.sizeM);
		ghost.visible = true;
		(ghost.material as THREE.MeshBasicMaterial).color.setHex(
			preview.valid ? COLOR_OK : COLOR_VIOLATION,
		);
	}

	private handlePointerMove(event: PointerEvent): void {
		if (!this.isDragging) return;
		if (
			this.activePointerId !== null &&
			event.pointerId !== this.activePointerId
		) {
			return;
		}
		const point = this.pointAt(event.clientX, event.clientY);
		if (!point) return;
		this.publish(this.resolveAt(point));
		event.preventDefault();
	}

	private handlePointerUp(event: PointerEvent): void {
		if (!this.isDragging) return;
		if (
			this.activePointerId !== null &&
			event.pointerId !== this.activePointerId
		) {
			return;
		}

		// Resolve from the RELEASE position rather than trusting the last move.
		// Move events are coalesced and can be dropped under load, and committing a
		// stale position would land the element somewhere the user did not let go
		// of.
		const preview =
			this.resolveAt(this.pointAt(event.clientX, event.clientY)) ??
			this.published;

		const committed =
			preview?.valid === true &&
			!interiorLayoutsEqual(preview.layout, this.config.setup.model.layout);

		this.cleanup();
		this.config.onPreviewChange?.(null);

		if (committed && preview) {
			this.config.onCommit(preview.layout);
		} else {
			// Nothing to undo and nothing to restore — the store never moved. The
			// owner still needs to hear it so orbit controls come back.
			this.config.onCancel();
		}
	}

	private handlePointerCancel(event: PointerEvent): void {
		if (!this.isDragging) return;
		if (
			this.activePointerId !== null &&
			event.pointerId !== this.activePointerId
		) {
			return;
		}
		this.cancelDrag();
	}

	private handleKeyDown(event: KeyboardEvent): void {
		if (event.key === "Escape") this.cancelDrag();
	}

	private cleanup(): void {
		if (this.activePointerId !== null) {
			try {
				this.domElement.releasePointerCapture(this.activePointerId);
			} catch {
				// Already released, or never captured.
			}
		}
		this.isDragging = false;
		this.activePointerId = null;
		this.origin = null;
		this.heightOrigin = null;
		this.published = null;

		this.domElement.removeEventListener("pointermove", this.boundPointerMove);
		this.domElement.removeEventListener("pointerup", this.boundPointerUp);
		this.domElement.removeEventListener(
			"pointercancel",
			this.boundPointerCancel,
		);
		this.domElement.removeEventListener(
			"lostpointercapture",
			this.boundPointerCancel,
		);
		window.removeEventListener("keydown", this.boundKeyDown);
		this.domElement.style.cursor = "";

		if (this.ghost) {
			this.ghost.removeFromParent();
			(this.ghost.material as THREE.Material).dispose();
			this.ghost = null;
		}
		for (const { mesh } of this.groupGhosts) {
			mesh.removeFromParent();
			(mesh.material as THREE.Material).dispose();
		}
		this.groupGhosts = [];
	}
}
